import { existsSync } from "fs"
import ora, { type Ora } from "ora"
import { BiomeInstaller } from "../features/code-quality/biome.js"
import { ESLintPrettierInstaller } from "../features/code-quality/eslint-prettier.js"
import { LefthookInstaller } from "../features/git-hooks/lefthook.js"
import { AstroFramework } from "../frameworks/astro/index.js"
import { ReactFramework } from "../frameworks/react/index.js"
import { SvelteFramework } from "../frameworks/svelte/index.js"
import { TailwindInstaller } from "../frameworks/tailwind/installer.js"
import { VanillaFramework } from "../frameworks/vanilla/index.js"
import { VueFramework } from "../frameworks/vue/index.js"
import {
  CLIError,
  type FeatureInstaller,
  type ProjectConfig,
} from "../types/types.js"
import { logger } from "../utils/logger.js"
import { PackageManagerAdapter } from "../utils/package-manager.js"

export class ProjectBuilder {
  private readonly config: ProjectConfig
  private rollbackActions: Array<() => Promise<void>> = []
  private spinner?: Ora
  private originalCwd: string = process.cwd()

  constructor(config: ProjectConfig) {
    this.config = config
  }

  async build(): Promise<void> {
    try {
      // Validate project doesn't already exist
      this.validateProject()

      // Create project structure
      await this.scaffoldProject()

      // Install dependencies
      await this.installDependencies()

      // Install features
      await this.installFeatures()

      // Initialize git repository
      await this.initializeGit()

      // Final setup
      this.finalizeSetup()
    } catch (error) {
      logger.error("Build failed, attempting rollback...")
      await this.rollback()
      process.chdir(this.originalCwd)
      throw error
    }
  }

  private validateProject(): void {
    if (existsSync(this.config.projectPath)) {
      throw new CLIError(
        `Directory ${this.config.projectName} already exists`,
        "DIRECTORY_EXISTS",
      )
    }
    if (!PackageManagerAdapter.isInstalled(this.config.packageManager)) {
      throw new CLIError(
        `Package manager ${this.config.packageManager} is not installed`,
        "PACKAGE_MANAGER_NOT_FOUND",
      )
    }
  }

  private async scaffoldProject(): Promise<void> {
    this.startSpinner("Creating project structure...")

    this.rollbackActions.push(async () => {
      const { rmSync } = await import("fs")
      rmSync(this.config.projectPath, { force: true, recursive: true })
    })

    const framework = this.getFramework()
    await framework.scaffold(this.config)

    this.succeedSpinner("Project structure created")
  }

  private installDependencies(): void {
    this.startSpinner("Installing dependencies...")

    const pm = new PackageManagerAdapter(this.config.packageManager)
    pm.install(this.config)

    this.succeedSpinner("Dependencies installed")
  }

  private async installFeatures(): Promise<void> {
    const features = this.getFeatures()

    for (const feature of features) {
      this.startSpinner(`Installing ${feature.name}...`)

      try {
        if (feature.validate && !feature.validate(this.config)) {
          this.skipSpinner(`${feature.name} not compatible with current setup`)
          continue
        }

        await feature.install(this.config)
        this.succeedSpinner(`${feature.name} installed`)
      } catch (error) {
        this.failSpinner(`Failed to install ${feature.name}`)
        throw error
      }
    }
  }

  private async initializeGit(): Promise<void> {
    if (this.config.dryRun) {
      logger.debug("[DRY RUN] Would initialize git repository")
      return
    }

    this.startSpinner("Initializing git repository...")

    try {
      const { execSync } = await import("child_process")
      execSync("git init", {
        cwd: this.config.projectPath,
        stdio: "ignore",
      })

      // Create .gitignore if it doesn't exist
      const gitignorePath = `${this.config.projectPath}/.gitignore`
      if (!existsSync(gitignorePath)) {
        const { writeFileSync } = await import("fs")
        writeFileSync(gitignorePath, this.getGitignoreContent())
      }

      this.succeedSpinner("Git repository initialized")
    } catch {
      this.skipSpinner("Git initialization skipped")
    }
  }

  private finalizeSetup(): void {
    // Any final setup tasks
    logger.debug("Finalizing project setup")
  }

  private getFramework() {
    const frameworks = {
      astro: AstroFramework,
      react: ReactFramework,
      svelte: SvelteFramework,
      vanilla: VanillaFramework,
      vue: VueFramework,
    }

    const FrameworkClass = frameworks[this.config.framework]
    if (!FrameworkClass) {
      throw new CLIError(
        `Unknown framework: ${this.config.framework}`,
        "UNKNOWN_FRAMEWORK",
      )
    }

    return new FrameworkClass()
  }

  private getFeatures(): Array<FeatureInstaller> {
    const features: Array<FeatureInstaller> = []

    if (this.config.tailwind) {
      features.push(new TailwindInstaller())
    }

    if (this.config.codeQuality === "biome") {
      features.push(new BiomeInstaller())
    } else {
      features.push(new ESLintPrettierInstaller())
    }

    if (this.config.gitHooks) {
      features.push(new LefthookInstaller())
    }

    return features
  }

  private getGitignoreContent(): string {
    return `# Dependencies
node_modules/
.pnp
.pnp.js

# Production
build/
dist/
out/
.next/
.nuxt/
.cache/

# Debug
npm-debug.log*
yarn-debug.log*
yarn-error.log*
pnpm-debug.log*

# Environment
.env
.env.local
.env.development.local
.env.test.local
.env.production.local

# IDE
.vscode/
.idea/
*.swp
*.swo
*.swn
*.bak
.DS_Store

# Testing
coverage/
.nyc_output/

# Misc
*.log
.eslintcache
.stylelintcache
`
  }

  private async rollback(): Promise<void> {
    for (const action of this.rollbackActions.reverse()) {
      try {
        await action()
      } catch (error) {
        logger.debug(`Rollback action failed: ${error.message}`)
      }
    }
  }

  private startSpinner(message: string): void {
    if (this.config.dryRun || this.config.debug) {
      logger.info(message)
      return
    }
    this.spinner = ora(message).start()
  }

  private succeedSpinner(message: string): void {
    if (this.spinner) {
      this.spinner.succeed(message)
      this.spinner = undefined
    } else {
      logger.success(message)
    }
  }

  private failSpinner(message: string): void {
    if (this.spinner) {
      this.spinner.fail(message)
      this.spinner = undefined
    } else {
      logger.error(message)
    }
  }

  private skipSpinner(message: string): void {
    if (this.spinner) {
      this.spinner.info(message)
      this.spinner = undefined
    } else {
      logger.info(message)
    }
  }
}
